/**
 * ActionPipeline.js — Single action editor showing WHEN -> PAY -> THEN flow
 *
 * Contains:
 * - Label text input + Variant dropdown
 * - Three PipelineSections: WHEN (conditions), PAY (costs), THEN (effects)
 * - PipelineConnectors between sections
 * - ToggleOffSection (collapsible)
 * - Delete Action button at bottom (with confirmation)
 */

import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { VALID_VARIANTS } from '../../../../engine/schema';
import PipelineSection, { COLOR_SCHEMES } from './PipelineSection';
import PipelineConnector from './PipelineConnector';
import ToggleOffSection from './ToggleOffSection';

const inputClass = cn(
  'bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white text-sm',
  'focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none w-full'
);

const labelClass = 'text-sm font-medium text-gray-300 mb-1 block';

const ActionPipeline = ({ action, actionIndex, dispatch, errors }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!action) return null;

  const handleFieldChange = (field, value) => {
    dispatch({ type: 'UPDATE_ACTION', actionIndex, field, value });
  };

  // ── Condition handlers ──
  const handleAddCondition = (newCondition) => {
    dispatch({ type: 'ADD_CONDITION', actionIndex, condition: newCondition });
  };

  const handleUpdateCondition = (conditionIndex, updated) => {
    dispatch({ type: 'UPDATE_CONDITION', actionIndex, conditionIndex, updates: updated });
  };

  const handleRemoveCondition = (conditionIndex) => {
    dispatch({ type: 'REMOVE_CONDITION', actionIndex, conditionIndex });
  };

  // ── Cost handlers ──
  const handleAddCost = (newCost) => {
    dispatch({ type: 'ADD_COST', actionIndex, cost: newCost });
  };

  const handleUpdateCost = (costIndex, updated) => {
    dispatch({ type: 'UPDATE_COST', actionIndex, costIndex, updates: updated });
  };

  const handleRemoveCost = (costIndex) => {
    dispatch({ type: 'REMOVE_COST', actionIndex, costIndex });
  };

  // ── Effect handlers ──
  const handleAddEffect = (newEffect) => {
    dispatch({ type: 'ADD_EFFECT', actionIndex, effect: newEffect });
  };

  const handleUpdateEffect = (effectIndex, updated) => {
    dispatch({ type: 'UPDATE_EFFECT', actionIndex, effectIndex, updates: updated });
  };

  const handleRemoveEffect = (effectIndex) => {
    dispatch({ type: 'REMOVE_EFFECT', actionIndex, effectIndex });
  };

  // ── Delete ──
  const handleDelete = () => {
    dispatch({ type: 'DELETE_ACTION', actionIndex });
    setShowDeleteConfirm(false);
  };

  return (
    <div className="space-y-1">
      {/* Action fields */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className={labelClass}>Label</label>
          <input
            type="text"
            value={action.label || ''}
            onChange={(e) => handleFieldChange('label', e.target.value)}
            placeholder="e.g. Absorb (1S)"
            className={inputClass}
          />
          {errors?.label && (
            <div className="text-red-400 text-xs mt-1">{errors.label}</div>
          )}
        </div>
        <div>
          <label className={labelClass}>Variant</label>
          <select
            value={action.variant || 'primary'}
            onChange={(e) => handleFieldChange('variant', e.target.value)}
            className={inputClass}
          >
            {VALID_VARIANTS.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      {/* WHEN — Conditions */}
      <PipelineSection
        title="WHEN all of these are true:"
        items={action.conditions}
        colorScheme={COLOR_SCHEMES.when}
        chipType="condition"
        onAdd={handleAddCondition}
        onUpdate={handleUpdateCondition}
        onRemove={handleRemoveCondition}
      />

      <PipelineConnector />

      {/* PAY — Costs */}
      <PipelineSection
        title="PAY these costs:"
        items={action.costs}
        colorScheme={COLOR_SCHEMES.pay}
        chipType="cost"
        onAdd={handleAddCost}
        onUpdate={handleUpdateCost}
        onRemove={handleRemoveCost}
      />

      <PipelineConnector />

      {/* THEN — Effects */}
      <PipelineSection
        title="THEN apply these effects (in order):"
        items={action.effects}
        colorScheme={COLOR_SCHEMES.then}
        chipType="effect"
        onAdd={handleAddEffect}
        onUpdate={handleUpdateEffect}
        onRemove={handleRemoveEffect}
      />
      {errors?.effects && (
        <div className="text-red-400 text-xs mt-1">{errors.effects}</div>
      )}

      {/* Toggle Off */}
      <ToggleOffSection
        action={action}
        actionIndex={actionIndex}
        dispatch={dispatch}
      />

      {/* Delete Action */}
      <div className="pt-3 border-t border-gray-700 mt-3">
        {showDeleteConfirm ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-red-400">Delete this action?</span>
            <button
              type="button"
              onClick={handleDelete}
              className="px-3 py-1 text-sm rounded-md bg-red-600 text-white hover:bg-red-500 transition-colors"
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(false)}
              className="px-3 py-1 text-sm rounded-md bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="inline-flex items-center gap-1 px-3 py-1 text-sm text-red-400 hover:text-red-300 transition-colors"
          >
            <Trash2 size={14} />
            Delete Action
          </button>
        )}
      </div>
    </div>
  );
};

export default ActionPipeline;
